import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { BuildingDto, MeterRoundDetailDto } from "@klantpunt/shared";
import { api } from "../../lib/api";
import { useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { buildingKey } from "../building/useBuilding";
import { roundKey } from "./useMeterRound";

/**
 * PDF-rapport van een ronde (docs/METERSTANDEN.md §9a), vanuit de rondepagina én de lijst met rondes.
 * Haalt de ronde en het gebouw (tekeningen) op als die nog niet geladen zijn; zonder gebouw (offline)
 * wordt het rapport zonder plattegrond.
 */
export function useRoundPdf() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  async function make(roundId: string) {
    setBusy(roundId);
    try {
      const [{ buildMeterRoundReport, meterRoundFilename }, web, d] = await Promise.all([
        import("../report/buildMeterRound"),
        import("../report/browser"),
        qc.fetchQuery({
          queryKey: roundKey(roundId),
          queryFn: () => api<MeterRoundDetailDto>(`/meters/rounds/${roundId}`),
        }),
      ]);
      const building = await qc
        .fetchQuery({
          queryKey: buildingKey(d.building.id),
          queryFn: () => api<BuildingDto>(`/buildings/${d.building.id}`),
        })
        .catch(() => null);
      const res = await buildMeterRoundReport({
        detail: d,
        plan: building ? { building, loadPlan: web.loadPlanRaster } : undefined,
        author: me?.user.name ?? "",
        date: new Date(),
        assets: await web.loadAssets(me?.org),
        loadPhoto: web.loadPhoto4x3,
      });
      web.download(res.bytes, meterRoundFilename(d.building.code, d.round.name));
      toast(`Rapport als PDF gemaakt (${res.pages} pagina's)`);
    } catch {
      toast("De PDF kon niet worden gemaakt. Probeer het opnieuw.");
    } finally {
      setBusy(null);
    }
  }
  return { make, busy };
}
